import React, {useCallback, FC, useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {useSelector, useDispatch} from 'react-redux';
import useNavigator from '@stencil/hotels-hooks/src/useNavigator';

//Components
import AlternateDatesUI from '../../../../../stories/components/hotels/DetailsPageUnif/AlternateDatesUI/AlternateDatesUI';

import {
  alternateDatesRequestSelector,
  alternateDatesSelector,
  hotelDetailsSelector,
  hotelNameSelector,
  isAltAccoSelector,
  roomsDataSelector,
  staticDataSelector,
  propertyUnavailableSelector,
  searchRoomApiRequestSelector,
} from '@stencil/hotels-cg-reducer/src/details/selectors';
import {
  commonSelectorFn,
  selectedHotelSelector,
} from '@stencil/hotels-common-reducer/src/selectors';

import {IUpdate_Query, UPDATE_QUERY} from '@stencil/hotels-common-reducer/src/types';

import {detailsPagePushGaThunk} from '@stencil/hotels-ga/src';
import {gaConfig} from '@stencil/hotels-config';
import {resetStaticData, resetSearchRoomData} from '@stencil/hotels-cg-reducer/src/details/actions';
import {omnitureThunk, omnitureConfig} from '@stencil/omniture-analytics/src';
import {
  CommonPageComponents,
  CommonPageEvents,
} from '@stencil/omniture-analytics/src/constants/common';
import PropertyUnavailableUI from '../../../../../stories/components/hotels/DetailsPageUnif/PropertyUnavailableUI/PropertyUnavailableUI';
import {useNavigateForSearchWidget} from '../../../../hooks/useNavigateForSearchWidget';
import {updateSRPCgConfig} from '@stencil/hotels-cg-reducer/src/srp/actions';

const {DetailsGAClickEventActions, DetailsGAEvents} = gaConfig;

function AlternateDates() {
  const params = useParams();
  const dispatch = useDispatch();
  const [navigateTo] = useNavigator();
  const {onSearch} = useNavigateForSearchWidget();

  const alternateDatesData = useSelector(alternateDatesSelector);
  const staticHotelDetailsData = useSelector(hotelDetailsSelector);
  const searchRoomData = useSelector(roomsDataSelector);

  const selectedHotel = useSelector(selectedHotelSelector);
  const alternateDatesApiRequest = useSelector(alternateDatesRequestSelector);

  const propertyName = useSelector(hotelNameSelector);
  const {checkin, checkout} = useSelector(commonSelectorFn);
  const isAtlAcoo = useSelector(isAltAccoSelector);
  const isPropertyUnavailable = useSelector(propertyUnavailableSelector);
  const searchRoomApiRequest = useSelector(searchRoomApiRequestSelector);
  const isAlternateDatesLoading = alternateDatesApiRequest?.loading;
  const {hotelId} = params;

  useEffect(() => {
    const altDateShownEvent = {
      event: DetailsGAEvents.click,
      eventCategory: gaConfig.detailsPageClickEventCategoryName,
      eventAction: DetailsGAClickEventActions.altDateShown,
      eventLabel: 'alternate_date_shown',
    };

    dispatch(detailsPagePushGaThunk(altDateShownEvent));
  }, []);

  const updateQueryCaller = useCallback(
    (actionData: IUpdate_Query['actionData']) => {
      return dispatch({type: UPDATE_QUERY, actionData});
    },
    [dispatch],
  );

  const resetCgDetailsCaller = useCallback(() => {
    dispatch(resetStaticData());
    dispatch(resetSearchRoomData());
  }, []);

  const alternateDateHandler = (checkIn, checkOut) => {
    staticHotelDetailsData && resetCgDetailsCaller();
    updateQueryCaller({
      checkin: {date: checkIn, isMachineGenerated: false},
      checkout: {date: checkOut, isMachineGenerated: false},
    });

    dispatch(
      omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
        cta: {
          name: `${CommonPageComponents.AlternateDates}_${checkIn}_${checkOut}`,
          type: omnitureConfig.ctaType.Card,
          componentName: CommonPageComponents.AlternateDates,
          destination: `hotel_${selectedHotel?.propertyName}_${selectedHotel?.propertyId}`,
        },
      }),
    );
    if (selectedHotel.propertyId && selectedHotel.propertyName) {
      navigateTo({target: 'details', replaceRoute: true, doNotSaveRs: true});
    }
  };

  const handlRedirectToSRPGOSuggest = useCallback(() => {
    dispatch(
      updateSRPCgConfig({
        metaPartner: 'goSuggest',
      }),
    );
    dispatch(
      omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
        cta: {
          name: CommonPageEvents.viewSimilarPropertiesClicked,
          type: omnitureConfig.ctaType.Card,
          componentName: CommonPageEvents.viewSimilarPropertiesClicked,
          destination: `hotel_${selectedHotel?.propertyName}_${selectedHotel?.propertyId}`,
        },
      }),
    );
    onSearch({
      openInNewTab: false,
      detailToSrpFlag: true,
      openSRPForHotelSearch: true,
    });
  }, [selectedHotel?.propertyName, selectedHotel?.propertyId]);

  if (isPropertyUnavailable) {
    return (
      <PropertyUnavailableUI
        handlRedirectToSRPGOSuggest={handlRedirectToSRPGOSuggest}
        errorMessage={searchRoomApiRequest?.error?.message}
      />
    );
  }

  return (
    <AlternateDatesUI
      data={alternateDatesData}
      alternateDateHandler={alternateDateHandler}
      checkin={checkin}
      checkout={checkout}
      propertyName={propertyName}
      isAltacco={isAtlAcoo}
      isAlternateDatesLoading={isAlternateDatesLoading}
      handlRedirectToSRPGOSuggest={handlRedirectToSRPGOSuggest}
    />
  );
}

export default AlternateDates;
